import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Gamepad2, Waves, Heart, Trophy, Star, Zap, Fish, Award, ChevronRight } from 'lucide-react';
import { useGameProgress } from '@/games/hooks';

// Animated otter swimming SVG
function SwimmingOtter({ className = '' }: { className?: string }) {
  return (
    <motion.svg
      viewBox="0 0 120 60"
      className={className}
      animate={{ y: [0, -5, 0], rotate: [0, 2, 0, -2, 0] }}
      transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
    >
      {/* Body */}
      <ellipse cx="60" cy="30" rx="35" ry="18" fill="#8B6914" />
      {/* Head */}
      <circle cx="95" cy="28" r="15" fill="#A0752E" />
      {/* Belly */}
      <ellipse cx="55" cy="35" rx="25" ry="10" fill="#D4B896" />
      {/* Face patch */}
      <ellipse cx="98" cy="30" rx="10" ry="8" fill="#D4B896" />
      {/* Eyes */}
      <circle cx="100" cy="25" r="3" fill="#333" />
      <circle cx="101" cy="24" r="1" fill="#fff" />
      {/* Nose */}
      <ellipse cx="108" cy="28" rx="4" ry="3" fill="#333" />
      {/* Whiskers */}
      <line x1="108" y1="30" x2="118" y2="28" stroke="#333" strokeWidth="0.5" />
      <line x1="108" y1="32" x2="118" y2="34" stroke="#333" strokeWidth="0.5" />
      {/* Ears */}
      <circle cx="88" cy="16" r="4" fill="#8B6914" />
      <circle cx="96" cy="14" r="4" fill="#8B6914" />
      {/* Tail */}
      <ellipse cx="20" cy="30" rx="12" ry="6" fill="#7A5A12" />
      {/* Paws */}
      <motion.ellipse
        cx="45"
        cy="45"
        rx="8"
        ry="5"
        fill="#7A5A12"
        animate={{ rotate: [0, 20, 0] }}
        transition={{ duration: 0.5, repeat: Infinity }}
      />
      <motion.ellipse
        cx="75"
        cy="45"
        rx="8"
        ry="5"
        fill="#7A5A12"
        animate={{ rotate: [0, -20, 0] }}
        transition={{ duration: 0.5, repeat: Infinity, delay: 0.25 }}
      />
    </motion.svg>
  );
}

// Animated water waves background
function WaterWaves() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-cyan-400/20 via-transparent to-blue-600/30" />

      {/* Animated waves */}
      {[0, 1, 2].map(i => (
        <motion.div
          key={i}
          className="absolute w-[200%] h-24"
          style={{
            bottom: `${i * 60}px`,
            left: '-50%',
            background: `linear-gradient(90deg, transparent, rgba(255,255,255,${0.05 + i * 0.02}), transparent)`,
            borderRadius: '50%',
          }}
          animate={{
            x: ['-25%', '0%', '-25%'],
          }}
          transition={{
            duration: 8 + i * 2,
            repeat: Infinity,
            ease: 'linear',
          }}
        />
      ))}

      {/* Floating bubbles */}
      {[...Array(8)].map((_, i) => (
        <motion.div
          key={`bubble-${i}`}
          className="absolute rounded-full bg-white/10"
          style={{
            width: 8 + Math.random() * 12,
            height: 8 + Math.random() * 12,
            left: `${10 + i * 12}%`,
            bottom: -20,
          }}
          animate={{
            y: [0, -400],
            opacity: [0, 0.6, 0],
          }}
          transition={{
            duration: 4 + Math.random() * 3,
            repeat: Infinity,
            delay: i * 0.8,
            ease: 'easeOut',
          }}
        />
      ))}
    </div>
  );
}

// Game preview illustration component
function GamePreview({ type }: { type: 'river-rush' | 'rescue-mission' }) {
  if (type === 'river-rush') {
    return (
      <div className="relative h-32 overflow-hidden rounded-xl bg-gradient-to-b from-cyan-500 to-blue-700">
        {/* Water */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-400/30 via-transparent to-blue-400/30" />

        {/* Fish */}
        <motion.div
          className="absolute text-orange-400"
          style={{ left: '70%', top: '40%' }}
          animate={{ x: [-100, 20], opacity: [0, 1, 1, 0] }}
          transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
        >
          <Fish className="w-6 h-6" />
        </motion.div>

        {/* Another fish */}
        <motion.div
          className="absolute text-yellow-400"
          style={{ left: '80%', top: '60%' }}
          animate={{ x: [-80, 40], opacity: [0, 1, 1, 0] }}
          transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 0.5, delay: 0.7 }}
        >
          <Fish className="w-5 h-5" />
        </motion.div>

        {/* Otter */}
        <motion.div
          className="absolute"
          style={{ left: '15%', top: '25%' }}
          animate={{ y: [0, -8, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <SwimmingOtter className="w-24 h-12" />
        </motion.div>

        {/* Obstacles */}
        <motion.div
          className="absolute bg-green-800 rounded"
          style={{ width: 40, height: 12, right: '-10%', top: '70%' }}
          animate={{ x: [-20, -200] }}
          transition={{ duration: 3, repeat: Infinity }}
        />

        {/* Speed lines */}
        <div className="absolute inset-0">
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute h-0.5 bg-white/20 rounded-full"
              style={{
                width: 20 + Math.random() * 40,
                left: `${20 + i * 15}%`,
                top: `${20 + i * 15}%`,
              }}
              animate={{ x: [-50, -150], opacity: [0, 0.5, 0] }}
              transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.2 }}
            />
          ))}
        </div>
      </div>
    );
  }

  // Rescue Mission preview
  return (
    <div className="relative h-32 overflow-hidden rounded-xl bg-gradient-to-b from-teal-500 to-green-700">
      {/* Shore/grass */}
      <div className="absolute bottom-0 left-0 right-0 h-8 bg-green-800" />

      {/* Hearts floating up */}
      {[0, 1, 2].map(i => (
        <motion.div
          key={i}
          className="absolute text-pink-400"
          style={{ left: `${30 + i * 20}%`, bottom: '20%' }}
          animate={{ y: [0, -40], opacity: [1, 0], scale: [0.8, 1.2] }}
          transition={{ duration: 2, repeat: Infinity, delay: i * 0.6 }}
        >
          <Heart className="w-5 h-5 fill-current" />
        </motion.div>
      ))}

      {/* Rescued otter */}
      <motion.div
        className="absolute"
        style={{ left: '35%', bottom: '25%' }}
        animate={{ rotate: [-5, 5, -5] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="relative">
          <div className="w-14 h-10 bg-amber-700 rounded-full" />
          <div className="absolute -right-2 top-1 w-8 h-8 bg-amber-600 rounded-full" />
          <div className="absolute right-0 top-2 w-5 h-4 bg-amber-200/80 rounded-full" />
          <div className="absolute right-1 top-2 w-1.5 h-1.5 bg-black rounded-full" />
        </div>
      </motion.div>

      {/* Medical cross */}
      <motion.div
        className="absolute bg-white/90 rounded p-1"
        style={{ right: '20%', top: '20%' }}
        animate={{ scale: [1, 1.1, 1] }}
        transition={{ duration: 1.5, repeat: Infinity }}
      >
        <div className="w-6 h-6 relative">
          <div className="absolute top-1/2 left-0 right-0 h-1.5 bg-red-500 -translate-y-1/2 rounded" />
          <div className="absolute left-1/2 top-0 bottom-0 w-1.5 bg-red-500 -translate-x-1/2 rounded" />
        </div>
      </motion.div>

      {/* Stars */}
      {[0, 1, 2].map(i => (
        <motion.div
          key={i}
          className="absolute"
          style={{ left: `${15 + i * 8}%`, top: '15%' }}
          animate={{ rotate: 360, scale: [0.8, 1, 0.8] }}
          transition={{ duration: 3, repeat: Infinity, delay: i * 0.3 }}
        >
          <Star className={`w-4 h-4 ${i < 2 ? 'text-yellow-400 fill-yellow-400' : 'text-yellow-400/30'}`} />
        </motion.div>
      ))}
    </div>
  );
}

export function Games() {
  const navigate = useNavigate();
  const { progress } = useGameProgress();

  const games = [
    {
      id: 'river-rush',
      title: 'Otter River Rush',
      description: 'Swim through rivers, collect fish, and dodge obstacles in this endless runner!',
      icon: Waves,
      gradient: 'from-cyan-500 via-blue-500 to-blue-700',
      accentColor: 'cyan',
      stats: [
        { icon: Trophy, label: 'High Score', value: progress.riverRush.highScore.toLocaleString(), color: 'text-yellow-400' },
        { icon: Zap, label: 'Distance', value: `${Math.floor(progress.riverRush.totalDistance)}m`, color: 'text-cyan-300' },
        { icon: Fish, label: 'Fish', value: progress.riverRush.totalFoodCollected.toString(), color: 'text-orange-400' },
      ],
      path: '/games/river-rush',
      unlocks: `${progress.riverRush.unlockedOtters.length + 1}/6 otters unlocked`,
    },
    {
      id: 'rescue-mission',
      title: 'Otter Rescue Mission',
      description: 'Help rehabilitate injured otters through fun mini-games and release them back to the wild!',
      icon: Heart,
      gradient: 'from-teal-500 via-emerald-500 to-green-700',
      accentColor: 'emerald',
      stats: [
        { icon: Heart, label: 'Rescues', value: progress.rescueMission.totalRescues.toString(), color: 'text-pink-400' },
        { icon: Star, label: 'Stars', value: progress.rescueMission.stars.toString(), color: 'text-yellow-400' },
        { icon: Award, label: 'Cards', value: progress.rescueMission.rescuedOtters.length.toString(), color: 'text-purple-400' },
      ],
      path: '/games/rescue-mission',
      unlocks: `Rescuer rank: ${progress.rescueMission.rescuerRank || 'Volunteer'}`,
    },
  ];

  const totalGamesPlayed = progress.riverRush.gamesPlayed + progress.rescueMission.totalRescues;
  const totalRewards = progress.rescueMission.stars + progress.riverRush.fishTokens;

  return (
    <div className="min-h-screen bg-gradient-to-b from-otter-700 via-otter-800 to-otter-950 relative overflow-hidden">
      <WaterWaves />

      <div className="relative z-10 p-4 pb-24">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-6"
        >
          <div className="flex items-center gap-3">
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
            >
              <Gamepad2 className="w-10 h-10 text-cyan-400" />
            </motion.div>
            <div>
              <h1 className="text-3xl font-bold text-white">Otter Games</h1>
              <p className="text-cyan-200/70 text-sm">Play & Learn!</p>
            </div>
          </div>

          {/* Total stats badge */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, type: 'spring' }}
            className="bg-black/30 backdrop-blur-sm rounded-2xl px-4 py-2 border border-white/10"
          >
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-400" />
              <span className="text-white font-bold">{totalGamesPlayed}</span>
            </div>
          </motion.div>
        </motion.div>

        {/* Featured otter animation */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="flex justify-center mb-6"
        >
          <div className="relative">
            <SwimmingOtter className="w-32 h-16" />
            <motion.div
              className="absolute -top-2 -right-2"
              animate={{ scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <span className="text-2xl">🎮</span>
            </motion.div>
          </div>
        </motion.div>

        {/* Game Cards */}
        <div className="space-y-6">
          {games.map((game, index) => (
            <motion.div
              key={game.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.15 }}
            >
              <motion.button
                onClick={() => navigate(game.path)}
                className="w-full text-left"
                whileTap={{ scale: 0.98 }}
              >
                <div className={`bg-gradient-to-br ${game.gradient} rounded-3xl overflow-hidden shadow-2xl border border-white/10`}>
                  {/* Preview illustration */}
                  <GamePreview type={game.id as 'river-rush' | 'rescue-mission'} />

                  {/* Content */}
                  <div className="p-5">
                    {/* Title row */}
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="bg-white/20 backdrop-blur-sm rounded-xl p-2.5">
                          <game.icon className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h2 className="text-xl font-bold text-white">{game.title}</h2>
                          <p className="text-white/60 text-xs">{game.unlocks}</p>
                        </div>
                      </div>
                      <motion.div
                        animate={{ x: [0, 5, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                        className="bg-white/20 rounded-full p-2"
                      >
                        <ChevronRight className="w-5 h-5 text-white" />
                      </motion.div>
                    </div>

                    {/* Description */}
                    <p className="text-white/80 text-sm mb-4 leading-relaxed">
                      {game.description}
                    </p>

                    {/* Stats row */}
                    <div className="flex gap-2">
                      {game.stats.map(stat => (
                        <div
                          key={stat.label}
                          className="flex-1 bg-black/20 backdrop-blur-sm rounded-xl px-3 py-2"
                        >
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <stat.icon className={`w-3.5 h-3.5 ${stat.color}`} />
                            <span className="text-white/50 text-xs">{stat.label}</span>
                          </div>
                          <p className="text-white font-bold text-lg">{stat.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.button>
            </motion.div>
          ))}
        </div>

        {/* Achievement summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-8"
        >
          <h3 className="text-white/60 text-sm font-medium mb-3 flex items-center gap-2">
            <Award className="w-4 h-4" />
            Your Gaming Journey
          </h3>
          <div className="bg-black/30 backdrop-blur-sm rounded-2xl p-5 border border-white/10">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity, repeatDelay: 2 }}
                >
                  <Gamepad2 className="w-8 h-8 text-cyan-400 mx-auto mb-2" />
                </motion.div>
                <p className="text-white font-bold text-xl">{totalGamesPlayed}</p>
                <p className="text-white/50 text-xs">Games Played</p>
              </div>
              <div className="text-center">
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <Star className="w-8 h-8 text-yellow-400 mx-auto mb-2 fill-yellow-400" />
                </motion.div>
                <p className="text-white font-bold text-xl">{totalRewards}</p>
                <p className="text-white/50 text-xs">Total Rewards</p>
              </div>
              <div className="text-center">
                <motion.div
                  animate={{ y: [0, -3, 0] }}
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  <Fish className="w-8 h-8 text-orange-400 mx-auto mb-2" />
                </motion.div>
                <p className="text-white font-bold text-xl">
                  {progress.riverRush.totalFoodCollected}
                </p>
                <p className="text-white/50 text-xs">Fish Collected</p>
              </div>
            </div>

            {/* Progress bar to next milestone */}
            {totalGamesPlayed < 50 && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <div className="flex justify-between text-xs text-white/50 mb-2">
                  <span>Progress to Gaming Pro</span>
                  <span>{totalGamesPlayed}/50 games</span>
                </div>
                <div className="h-2 bg-black/30 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${(totalGamesPlayed / 50) * 100}%` }}
                    transition={{ delay: 0.8, duration: 0.5 }}
                  />
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Coming soon teaser */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-6"
        >
          <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-2xl p-4 border border-dashed border-white/20">
            <div className="flex items-center justify-center gap-3">
              <motion.span
                animate={{ rotate: [0, 20, -20, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="text-2xl"
              >
                🦦
              </motion.span>
              <p className="text-white/60 text-sm font-medium">
                More otter games coming soon!
              </p>
              <motion.span
                animate={{ rotate: [0, -20, 20, 0] }}
                transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                className="text-2xl"
              >
                🎮
              </motion.span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
